// KPI tile — small uppercase label, big mono number, accent left-border on hover.
// Phase 9: optional count-up animation on initial mount when value is a finite
// number. Strings ("timeout", "—") render as-is.
import { useEffect, useRef, useState } from "react";

function isFiniteNumber(v) {
    return typeof v === "number" && Number.isFinite(v);
}

function formatTransient(v, isInteger) {
    if (isInteger) return String(Math.floor(v));
    return v.toFixed(1);
}

export default function KpiTile({
    label,
    value,
    hint,
    testId,
    className = "",
    animate = true,
}) {
    const target = isFiniteNumber(value) ? value : null;
    const isInteger = target !== null && Number.isInteger(target);
    const [display, setDisplay] = useState(target === null ? null : 0);
    const rafRef = useRef(0);

    useEffect(() => {
        if (target === null) {
            setDisplay(null);
            return undefined;
        }
        if (!animate) {
            setDisplay(target);
            return undefined;
        }
        const start = performance.now();
        const dur = 600;
        const from = 0;
        const to = target;

        const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic
        const tick = (now) => {
            const elapsed = now - start;
            const t = Math.min(1, elapsed / dur);
            const v = from + (to - from) * ease(t);
            setDisplay(t === 1 ? to : v);
            if (t < 1) rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
        // Re-run only when the underlying numeric target changes.
    }, [target, animate]);

    let renderValue;
    if (target === null) {
        renderValue = value ?? "—";
    } else if (display === null) {
        renderValue = "—";
    } else if (display === target) {
        // Final state — show the original numeric value (no transient .toFixed).
        renderValue = String(target);
    } else {
        renderValue = formatTransient(display, isInteger);
    }

    return (
        <div
            data-testid={testId}
            className={[
                "min-w-[160px] bg-ux-bg-1 border border-ux-border-1 rounded-md px-4 py-3",
                "border-l-2 border-l-transparent hover:border-l-ux-accent transition-colors",
                className,
            ].join(" ")}
        >
            <div className="label-mini">{label}</div>
            <div className="mt-1 font-mono tabular-nums text-[24px] leading-tight text-ux-text-1 break-all">
                {renderValue}
            </div>
            {hint && <div className="mt-1 text-[11px] text-ux-text-3">{hint}</div>}
        </div>
    );
}
